import { isAfter, parseISO } from "date-fns";
import { List, Map } from "immutable";
import moment from "moment";

import {
  getAverageScore,
  getSumQuestionMarks,
  getSumQuestionMarksTotal,
  getTotalStudyTime
} from "seneca-common/features/assignments/features/teacher-assignments/state/selectors/state/assignments/assignment/assignees/aggregatedStats";
import { getAssignmentDueDate } from "seneca-common/features/assignments/state";
import { composeSelector } from "seneca-common/utils/selectors/compose-selectors";

import { EmbeddedAnswerMark } from "../../../models/Assignee/QuestionStat";
import TeacherAssignment from "../../../models/TeacherAssignment";
import { getQuestionStats } from "./assignment/assignees/individualAssignee";
import {
  getConfidence,
  getEmbeddedAnswerMarks,
  getFlagged,
  getMark,
  getMarksTotal,
  getNeedsMarking,
  getSelfMark,
  getTeacherMark
} from "./assignment/assignees/questionStats";
import * as smartAssignments from "./assignment/smartAssignment";
import * as teacherAssignment from "./assignment/teacherAssignment";

import type { Condition } from "./assignment/smartAssignment";
export { getAssignmentDueDate };

export {
  getAssignment,
  getAssignmentSectionLastSetDate,
  getAssignmentStartDate,
  getAssignmentName,
  getAssignmentNumOfAssignees,
  getAssignmentCourseId,
  getAssignmentSectionIds,
  getAssignmentExamQuestionIds,
  getAssignmentNumberOfSections,
  getAssignmentNumberOfExamQuestions,
  getAssignmentCutoffDate,
  getClassAssignmentIds,
  getNumberOfClassAssignments,
  getNumberOfUpcomingClassAssignments,
  getNextDueAssignmentId,
  getNextDueAssignmentWithExamQuestions,
  getNumberOfUpcomingClassAssignmentsWithExamQuestions,
  getNumberOfPastClassAssignments,
  getAssignmentSpecType,
  getAssignmentIsSmart,
  assignmentIncludesExamQuestions,
  assignmentHasNoSections
} from "seneca-common/features/assignments/state";

type State = Map<string, TeacherAssignment>;
type AssignmentId = string;
type UserId = string;

function getAssignment(state: State, assignmentId: AssignmentId) {
  return state.get(assignmentId);
}

export const getAssignmentAssignees: (
  State: any,
  AssignmentId: any
) => List<UserId> = composeSelector(
  getAssignment as any,
  teacherAssignment.getAssigneeUserIds
);

export function getNumberOfAssignees(state: State, assignmentId: AssignmentId) {
  return getAssignmentAssignees(state, assignmentId).size;
}

export const assignmentExists = (
  state: State,
  assignmentId: AssignmentId
): boolean => !!state.get(assignmentId);

// @ts-ignore
export const getAutoEnrolNewStudents: (
  State: any,
  AssignmentId: any
) => boolean = composeSelector(
  getAssignment as any,
  teacherAssignment.getAutoEnrolNewUsers
);

export const getAssigneesWithSections: (
  State: any,
  AssignmentId: string
) => Map<string, List<string>> = composeSelector(
  getAssignment as any,
  teacherAssignment.getAssignmentAssigneesWithSections
);

export const getIsPastAssignment = (
  state: State,
  assignmentId: string,
  now: moment.Moment
) => {
  const assignment = getAssignment(state, assignmentId);
  return teacherAssignment.isPastAssignment(assignment as any, now);
};

export const getIsAssignmentAfterCutoffDate = (
  state: State,
  assignmentId: string,
  now: moment.Moment
) => {
  const assignment = getAssignment(state, assignmentId);
  return teacherAssignment.isAssignmentAfterCutoffDate(assignment as any, now);
};

export function getAssignmentSectionAverageScore(
  state: State,
  assignmentId: AssignmentId,
  sectionId: string
) {
  const assignment = getAssignment(state, assignmentId);

  return teacherAssignment.getSectionAverageScore(assignment as any, sectionId);
}

export function getAssignmentQuestionAverageMark(
  state: State,
  assignmentId: AssignmentId,
  questionId: string
) {
  const assignment = getAssignment(state, assignmentId);

  return teacherAssignment.getQuestionAverageMark(
    assignment as any,
    questionId
  );
}

export function getAssignmentQuestionMarksTotal(
  state: State,
  assignmentId: AssignmentId,
  questionId: string
) {
  const assignment = getAssignment(state, assignmentId);

  return teacherAssignment.getQuestionMarksTotal(assignment as any, questionId);
}

export function getAssignmentQuestionsAverageMark(
  state: State,
  assignmentId: AssignmentId,
  questionId: string
) {
  const assignment = getAssignment(state, assignmentId);

  return teacherAssignment.getQuestionsAverageMark(
    assignment as any,
    questionId
  );
}

export function getAssignmentQuestionsMarksTotal(
  state: State,
  assignmentId: AssignmentId,
  questionId: string
) {
  const assignment = getAssignment(state, assignmentId);

  return teacherAssignment.getQuestionsMarksTotal(
    assignment as any,
    questionId
  );
}

export const isAssignmentWithStats = composeSelector(
  getAssignment as any,
  teacherAssignment.isAssignmentWithStats
);

export const getAssignees = composeSelector(
  getAssignment as any,
  teacherAssignment.getAssignees
);

export function getAssigneeStatus(
  state: State,
  assignmentId: string,
  userId: string
) {
  const assignment = getAssignment(state, assignmentId);
  return teacherAssignment.getAssigneeStatus(assignment, userId);
}

export function getAssigneeAggregatedStats(
  state: State,
  assignmentId: string,
  userId: string
) {
  const assignment = getAssignment(state, assignmentId);
  return teacherAssignment.getAssigneeAggregatedStats(assignment, userId);
}

export function getAssigneeTotalStudyTime(
  state: State,
  assignmentId: string,
  userId: string
) {
  const aggregatedStats = getAssigneeAggregatedStats(
    state,
    assignmentId,
    userId
  );
  return getTotalStudyTime(aggregatedStats);
}

export function getAssigneeAverageScore(
  state: State,
  assignmentId: string,
  userId: string
) {
  const aggregatedStats = getAssigneeAggregatedStats(
    state,
    assignmentId,
    userId
  );
  return getAverageScore(aggregatedStats);
}

export function getAssigneeSumQuestionMarks(
  state: State,
  assignmentId: string,
  userId: string
) {
  const aggregatedStats = getAssigneeAggregatedStats(
    state,
    assignmentId,
    userId
  );
  return getSumQuestionMarks(aggregatedStats);
}

export function getAssigneeSumQuestionMarksTotal(
  state: State,
  assignmentId: string,
  userId: string
) {
  const aggregatedStats = getAssigneeAggregatedStats(
    state,
    assignmentId,
    userId
  );
  return getSumQuestionMarksTotal(aggregatedStats);
}

export function getAssigneeSectionStats(
  state: State,
  assignmentId: string,
  userId: string
) {
  const assignment = getAssignment(state, assignmentId);
  return teacherAssignment.getAssigneeSectionStats(assignment, userId);
}

export function getAssigneeQuestionStats(
  state: State,
  assignmentId: string,
  userId: string
) {
  const assignment = getAssignment(state, assignmentId);
  return teacherAssignment.getAssigneeQuestionStats(assignment, userId);
}

export function getAssignee(
  state: State,
  assignmentId: string,
  userId: string
) {
  const assignment = getAssignment(state, assignmentId);
  return teacherAssignment.getAssignee(assignment, userId);
}

export function getAssigneeIndividualSectionStats(
  state: State,
  assignmentId: string,
  userId: string,
  sectionId: string
) {
  const assignment = getAssignment(state, assignmentId);
  return teacherAssignment.getAssigneeIndividualSectionStats(
    assignment,
    userId,
    sectionId
  );
}

export const getAssigneeQuestionTeacherMark: (
  state: any,
  assignmentId: string,
  userId: string,
  questionId: string
) => number | undefined = composeSelector(
  getAssigneeIndividualQuestionStats,
  getTeacherMark
);

export const getAssigneeQuestionEmbeddedAnswerMarks: (
  state: any,
  assignmentId: string,
  userId: string,
  questionId: string
) => EmbeddedAnswerMark[] | undefined = composeSelector(
  getAssigneeIndividualQuestionStats,
  getEmbeddedAnswerMarks
);

export const getAssigneeQuestionAutoMark: (
  state: any,
  assignmentId: string,
  userId: string,
  questionId: string
) => number | undefined = composeSelector(
  getAssigneeIndividualQuestionStats,
  getMark
);

export const getAssigneeQuestionFlagged: (
  state: any,
  assignmentId: string,
  userId: string,
  questionId: string
) => boolean | undefined = composeSelector(
  getAssigneeIndividualQuestionStats,
  s => !!getFlagged(s)
);

export const getAssigneeQuestionNeedsMarking: (
  state: any,
  assignmentId: string,
  userId: string,
  questionId: string
) => boolean = composeSelector(getAssigneeIndividualQuestionStats, s =>
  getNeedsMarking(s)
);

export const getAssigneeQuestionSelfMark: (
  state: any,
  assignmentId: string,
  userId: string,
  questionId: string
) => number | undefined = composeSelector(
  getAssigneeIndividualQuestionStats,
  getSelfMark
);

export const getAssigneeQuestionConfidence: (
  state: any,
  assignmentId: string,
  userId: string,
  questionId: string
) => number | undefined = composeSelector(
  getAssigneeIndividualQuestionStats,
  getConfidence
);

export const getAssigneeQuestionTotalMarks: (
  state: any,
  assignmentId: string,
  userId: string,
  questionId: string
) => number | undefined = composeSelector(
  getAssigneeIndividualQuestionStats,
  getMarksTotal
);

export function getAssigneeIndividualQuestionStats(
  state: State,
  assignmentId: string,
  userId: string,
  questionId: string
) {
  const assignment = getAssignment(state, assignmentId);
  return teacherAssignment.getAssigneeIndividualQuestionStats(
    assignment,
    userId,
    questionId
  );
}

export function getNumberOfAssigneesWithStatus(
  state: State,
  assignmentId: string,
  status: string
) {
  const assignment = getAssignment(state, assignmentId);
  return teacherAssignment.getNumberOfAssigneesWithStatus(
    assignment as any,
    status
  );
}

export function getNumberOfAssigneesInProgress(
  state: State,
  assignmentId: string
) {
  const assignment = getAssignment(state, assignmentId);
  return teacherAssignment.getNumberOfAssigneesInProgress(assignment as any);
}

export function getAssigneeSectionIds(
  state: State,
  assignmentId: string,
  userId: string
) {
  const assignment = getAssignment(state, assignmentId);
  return teacherAssignment.getAssigneeSectionIds(assignment as any, userId);
}

export function getIsAfterAssignmentDueDate(
  state: State,
  assignmentId: string,
  date: Date
) {
  const dueDate = getAssignmentDueDate(state as any, assignmentId);

  return isAfter(date, parseISO(dueDate));
}

export function getSmartAssignmentSectionsFromConditions(
  state: State,
  assignmentId: string,
  conditions: Condition[]
) {
  const assignment = getAssignment(state, assignmentId);

  return smartAssignments.getSmartAssignmentSectionsFromConditions(
    assignment as any,
    conditions
  );
}

export function getNumberOfAnswersNeedMarking(
  state: State,
  assignmentId: string,
  questionIds?: string[]
) {
  const assignees = getAssignees(state, assignmentId).valueSeq().toList();

  return assignees.reduce((totalNumberOfAnswersNeedMarking, assignee) => {
    const statsList = getQuestionStats(assignee)
      ?.toKeyedSeq()
      .filter((_, key) => (questionIds ? questionIds.includes(key) : true))
      .valueSeq()
      .toList();
    return statsList?.reduce(
      (numberOfAnswersNeedMarkingForAssignee, stat) =>
        numberOfAnswersNeedMarkingForAssignee + Number(getNeedsMarking(stat)),
      totalNumberOfAnswersNeedMarking
    )!;
  }, 0);
}

export function getNumberOfAnswersNotRemarked(
  state: State,
  assignmentId: string,
  questionIds?: string[]
) {
  const assignees = getAssignees(state, assignmentId).valueSeq().toList();

  return assignees.reduce((numOfAnswersNotRemarked, assignee) => {
    const statsList = getQuestionStats(assignee)
      ?.toKeyedSeq()
      .filter((_, key) => (questionIds ? questionIds.includes(key) : true))
      .valueSeq()
      .toList();
    const notRemarked =
      statsList?.filter(stat => getTeacherMark(stat) === undefined)?.size ?? 0;
    return numOfAnswersNotRemarked + notRemarked;
  }, 0);
}

export function getNumberOfStudentsNeedMarking(
  state: State,
  assignmentId: string
) {
  const assignees = getAssignees(state, assignmentId).valueSeq().toList();

  return assignees.reduce((numOfStudentsNeedMarking, assignee) => {
    const statsList = getQuestionStats(assignee)?.valueSeq().toList();
    const needsMarking = !!statsList?.find(getNeedsMarking);
    return numOfStudentsNeedMarking + Number(needsMarking);
  }, 0);
}
